'use client';

import React from 'react';
import Link from 'next/link';

interface FooterLink {
  label: string;
  href: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

const footerSections: FooterSection[] = [
  {
    title: 'Services',
    links: [
      { label: 'Service Plans', href: '/monthly-cleaning-plans' },
      { label: 'Apply Now', href: '/customer-application-form' }
    ]
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', href: '/about-us' },
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faq' }
    ]
  },
  {
    title: 'Legal',
    links: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' }
    ]
  }
];

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className={`bg-foreground ${className}`}>
      <div className="max-w-7xl mx-auto px-8 lg:px-16 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-16">
          <div className="lg:col-span-2">
            <Link href="/homepage" className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
                <svg
                  viewBox="0 0 40 40"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-7 h-7"
                >
                  <path
                    d="M20 8L8 16V32H16V24H24V32H32V16L20 8Z"
                    fill="currentColor"
                    className="text-primary-foreground"
                  />
                </svg>
              </div>
              <span className="text-xl font-headline font-semibold text-white">
                Ordify Clean
              </span>
            </Link>

            <p className="text-sm text-white/50 leading-relaxed font-body max-w-xs">
              Managed residential cleaning service powered by a vetted network of independent professionals.
            </p>
          </div>

          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-xs font-cta font-medium text-white/40 mb-6 uppercase tracking-[0.15em]">
                {section.title}
              </h3>
              <ul className="space-y-4">
                {section.links.map((link) => (
                  <li key={link.href}>
                    {link.href === '#' ? (
                      <span className="text-sm text-white/25 cursor-not-allowed font-body">
                        {link.label}
                      </span>
                    ) : (
                      <Link
                        href={link.href}
                        className="text-sm text-white/60 hover:text-white transition-colors duration-300 font-body"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8">
          <p className="text-xs text-white/30 font-body tracking-wide">
            &copy; {currentYear} Ordify Clean — Operated by Cleaning Certainty LLC
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;